import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _create_class from "@swc/helpers/src/_create_class.mjs";
import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import { Price } from "./fractions/price";
import { TokenAmount } from "./fractions/tokenAmount";
import invariant from "tiny-invariant";
import JSBI from "jsbi";
import { pack, keccak256 } from "@ethersproject/solidity";
import { getCreate2Address } from "@ethersproject/address";
import { FACTORY_ADDRESS_MAP, INIT_CODE_HASH_MAP, MINIMUM_LIQUIDITY, ZERO, ONE, FIVE, FEES_NUMERATOR, FEES_DENOMINATOR } from "../constants";
import { sqrt, parseBigintIsh } from "../utils";
import { InsufficientReservesError, InsufficientInputAmountError } from "../errors";
import { Token } from "./token";
var PAIR_ADDRESS_CACHE = {};
var composeKey = function(token0, token1) {
    return "".concat(token0.chainId, "-").concat(token0.address, "-").concat(token1.address);
};
export var Pair = /*#__PURE__*/ function() {
    "use strict";
    function Pair(tokenAmountA, tokenAmountB) {
        _class_call_check(this, Pair);
        var tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
         ? [
            tokenAmountA,
            tokenAmountB
        ] : [
            tokenAmountB,
            tokenAmountA
        ];
        this.liquidityToken = new Token(tokenAmounts[0].token.chainId, Pair.getAddress(tokenAmounts[0].token, tokenAmounts[1].token), 18, "EJOE-LP", "EJOE LPs");
        this.tokenAmounts = tokenAmounts;
    }
    var _proto = Pair.prototype;
    /**
   * Returns true if the token is either token0 or token1
   * @param token to check
   */ _proto.involvesToken = function involvesToken(token) {
        return token.equals(this.token0) || token.equals(this.token1);
    };
    /**
   * Return the price of the given token in terms of the other token in the pair.
   * @param token token to return price of
   */ _proto.priceOf = function priceOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.token0Price : this.token1Price;
    };
    _proto.reserveOf = function reserveOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.reserve0 : this.reserve1;
    };
    _proto.getOutputAmount = function getOutputAmount(inputAmount) {
        invariant(this.involvesToken(inputAmount.token), "TOKEN");
        if (JSBI.equal(this.reserve0.raw, ZERO) || JSBI.equal(this.reserve1.raw, ZERO)) {
            throw new InsufficientReservesError();
        }
        var inputReserve = this.reserveOf(inputAmount.token);
        var outputReserve = this.reserveOf(inputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var inputAmountWithFee = JSBI.multiply(inputAmount.raw, FEES_NUMERATOR);
        var numerator = JSBI.multiply(inputAmountWithFee, outputReserve.raw);
        var denominator = JSBI.add(JSBI.multiply(inputReserve.raw, FEES_DENOMINATOR), inputAmountWithFee);
        var outputAmount = new TokenAmount(inputAmount.token.equals(this.token0) ? this.token1 : this.token0, JSBI.divide(numerator, denominator));
        if (JSBI.equal(outputAmount.raw, ZERO)) {
            throw new InsufficientInputAmountError();
        }
        return [
            outputAmount,
            new Pair(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount))
        ];
    };
    _proto.getInputAmount = function getInputAmount(outputAmount) {
        invariant(this.involvesToken(outputAmount.token), "TOKEN");
        if (JSBI.equal(this.reserve0.raw, ZERO) || JSBI.equal(this.reserve1.raw, ZERO) || JSBI.greaterThanOrEqual(outputAmount.raw, this.reserveOf(outputAmount.token).raw)) {
            throw new InsufficientReservesError();
        }
        var outputReserve = this.reserveOf(outputAmount.token);
        var inputReserve = this.reserveOf(outputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var numerator = JSBI.multiply(JSBI.multiply(inputReserve.raw, outputAmount.raw), FEES_DENOMINATOR);
        var denominator = JSBI.multiply(JSBI.subtract(outputReserve.raw, outputAmount.raw), FEES_NUMERATOR);
        var inputAmount = new TokenAmount(outputAmount.token.equals(this.token0) ? this.token1 : this.token0, JSBI.add(JSBI.divide(numerator, denominator), ONE));
        return [
            inputAmount,
            new Pair(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount))
        ];
    };
    _proto.getLiquidityMinted = function getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB) {
        invariant(totalSupply.token.equals(this.liquidityToken), "LIQUIDITY");
        var tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
         ? [
            tokenAmountA,
            tokenAmountB
        ] : [
            tokenAmountB,
            tokenAmountA
        ];
        invariant(tokenAmounts[0].token.equals(this.token0) && tokenAmounts[1].token.equals(this.token1), "TOKEN");
        var liquidity;
        if (JSBI.equal(totalSupply.raw, ZERO)) {
            liquidity = JSBI.subtract(sqrt(JSBI.multiply(tokenAmounts[0].raw, tokenAmounts[1].raw)), MINIMUM_LIQUIDITY);
        } else {
            var amount0 = JSBI.divide(JSBI.multiply(tokenAmounts[0].raw, totalSupply.raw), this.reserve0.raw);
            var amount1 = JSBI.divide(JSBI.multiply(tokenAmounts[1].raw, totalSupply.raw), this.reserve1.raw);
            liquidity = JSBI.lessThanOrEqual(amount0, amount1) ? amount0 : amount1;
        }
        if (!JSBI.greaterThan(liquidity, ZERO)) {
            throw new InsufficientInputAmountError();
        }
        return new TokenAmount(this.liquidityToken, liquidity);
    };
    _proto.getLiquidityValue = function getLiquidityValue(token, totalSupply, liquidity) {
        var feeOn = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : false, kLast = arguments.length > 4 ? arguments[4] : void 0;
        invariant(this.involvesToken(token), "TOKEN");
        invariant(totalSupply.token.equals(this.liquidityToken), "TOTAL_SUPPLY");
        invariant(liquidity.token.equals(this.liquidityToken), "LIQUIDITY");
        invariant(JSBI.lessThanOrEqual(liquidity.raw, totalSupply.raw), "LIQUIDITY");
        var totalSupplyAdjusted;
        if (!feeOn) {
            totalSupplyAdjusted = totalSupply;
        } else {
            invariant(!!kLast, "K_LAST");
            var kLastParsed = parseBigintIsh(kLast);
            if (!JSBI.equal(kLastParsed, ZERO)) {
                var rootK = sqrt(JSBI.multiply(this.reserve0.raw, this.reserve1.raw));
                var rootKLast = sqrt(kLastParsed);
                if (JSBI.greaterThan(rootK, rootKLast)) {
                    var numerator = JSBI.multiply(totalSupply.raw, JSBI.subtract(rootK, rootKLast));
                    var denominator = JSBI.add(JSBI.multiply(rootK, FIVE), rootKLast);
                    var feeLiquidity = JSBI.divide(numerator, denominator);
                    totalSupplyAdjusted = totalSupply.add(new TokenAmount(this.liquidityToken, feeLiquidity));
                } else {
                    totalSupplyAdjusted = totalSupply;
                }
            } else {
                totalSupplyAdjusted = totalSupply;
            }
        }
        return new TokenAmount(token, JSBI.divide(JSBI.multiply(liquidity.raw, this.reserveOf(token).raw), totalSupplyAdjusted.raw));
    };
    Pair.getAddress = function getAddress(tokenA, tokenB) {
        var ref // does safety checks
         = _sliced_to_array(tokenA.sortsBefore(tokenB) ? [
            tokenA,
            tokenB
        ] : [
            tokenB,
            tokenA
        ], 2), token0 = ref[0], token1 = ref[1];
        var key = composeKey(token0, token1);
        if ((PAIR_ADDRESS_CACHE === null || PAIR_ADDRESS_CACHE === void 0 ? void 0 : PAIR_ADDRESS_CACHE[key]) === undefined) {
            PAIR_ADDRESS_CACHE = _object_spread_props(_object_spread({}, PAIR_ADDRESS_CACHE), _define_property({}, key, getCreate2Address(FACTORY_ADDRESS_MAP[token0.chainId], keccak256([
                "bytes"
            ], [
                pack([
                    "address",
                    "address"
                ], [
                    token0.address,
                    token1.address
                ])
            ]), INIT_CODE_HASH_MAP[token0.chainId])));
        }
        return PAIR_ADDRESS_CACHE[key];
    };
    _create_class(Pair, [
        {
            key: "token0Price",
            get: /**
   * Returns the current mid price of the pair in terms of token0, i.e. the ratio of reserve1 to reserve0
   */ function get() {
                return new Price(this.token0, this.token1, this.tokenAmounts[0].raw, this.tokenAmounts[1].raw);
            }
        },
        {
            key: "token1Price",
            get: /**
   * Returns the current mid price of the pair in terms of token1, i.e. the ratio of reserve0 to reserve1
   */ function get() {
                return new Price(this.token1, this.token0, this.tokenAmounts[1].raw, this.tokenAmounts[0].raw);
            }
        },
        {
            key: "chainId",
            get: /**
   * Returns the chain ID of the tokens in the pair.
   */ function get() {
                return this.token0.chainId;
            }
        },
        {
            key: "token0",
            get: function get() {
                return this.tokenAmounts[0].token;
            }
        },
        {
            key: "token1",
            get: function get() {
                return this.tokenAmounts[1].token;
            }
        },
        {
            key: "reserve0",
            get: function get() {
                return this.tokenAmounts[0];
            }
        },
        {
            key: "reserve1",
            get: function get() {
                return this.tokenAmounts[1];
            }
        }
    ]);
    return Pair;
}();
